import update from "immutability-helper";
import uniques from "../utils/uniques";
import {itemVoteChanged, loadCollection} from "./utils";

const listKeyOfComment = (comment) =>
    comment.comment_parent_id
        ? `c/${comment.comment_parent_id}`
        : `p/${comment.parent_id}`;

export const commentsLoaded = ({commentMap, ...state}, {comments}) => (
    {...state, commentMap: loadCollection(commentMap, comments, 'id')}
);

export const commentListLoading = ({commentLists, ...state}, {key}) => {
    commentLists = commentLists.update(key,
        (list) => ({...list, loading: true})
    );
    return ({...state, commentLists});
};

export const commentListLoaded = ({commentMap, commentLists, ...state}, {key, ids, comments, more}) => {
    // Compute the size (number of descendants) of each comment.
    // TODO: Comment size is not maintained when comments are added/removed later.
    const map = new Map();
    for (const comment of comments) {
        map.set(comment.id, comment);
    }
    function sizeComment(id) {
        const comment = map.get(id);
        let size = 0;
        if ('child_ids' in comment) {
            for (const childId of comment.child_ids) {
                size += 1 + sizeComment(childId);
            }
        }
        comment.size = size;
        return size;
    }
    for (const id of ids) {
        sizeComment(id);
    }
    commentMap = loadCollection(commentMap, comments, 'id');
    commentLists = commentLists.withMutations((lists) => {
        lists.set(key, {ids, page: 1, more, loading: false});
        for (const comment of comments) {
            if ('child_ids' in comment) {
                lists.set(`c/${comment.id}`, {ids: comment.child_ids});
            }
        }
    });
    return {...state, commentMap, commentLists};
};

export const commentListExtended = ({commentMap, commentLists, ...state}, {key, ids, comments, page, more}) => {
    commentMap = loadCollection(commentMap, comments, 'id');
    commentLists = commentLists.withMutations((lists) => {
        lists.update(key, ({ids: oldIds}) => ({ids: uniques([...oldIds, ...ids]), page, more, loading: false}));
        for (const comment of comments) {
            if ('child_ids' in comment) {
                lists.set(`c/${comment.id}`, {ids: comment.child_ids});
            }
        }
    });
    return {...state, commentMap, commentLists};
};

export const commentListLoadFailed = ({commentLists, ...state}, {key}) => {
    commentLists = commentLists.update(key,
        (list) => ({...list, loading: false, more: false})
    );
    return ({...state, commentLists});
};

export const commentCreated = ({commentMap, commentLists, ...state}, {comment}) => {
    commentMap = commentMap.set(comment.id, comment);
    commentLists = commentLists.withMutations((lists) => {
        const listKey = listKeyOfComment(comment);
        const list = lists.get(listKey) || {ids: []};
        lists.set(listKey, {...list, ids: [comment.id, ...list.ids]});
    });
    return {...state, commentMap, commentLists};
};

export const commentEdited = ({commentMap, ...state}, {id, content}) => {
    commentMap = commentMap.update(
        id,
        (comment) => ({...comment, content})
    );
    return {...state, commentMap};
};

export const commentVoteChanged = ({commentMap, ...state}, {id, voteState}) => {
    commentMap = commentMap.set(id, itemVoteChanged(commentMap.get(id), voteState));
    return {...state, commentMap};
};

export const commentDeleted = ({commentMap, ...state}, {id}) => {
    commentMap = commentMap.set(id, {...commentMap.get(id), is_deleted: true});
    return {...state, commentMap};
};

export const commentSticky = ({commentMap, ...state}, {id, newState}) => {
    commentMap = commentMap.set(id, {...commentMap.get(id), is_stickied: newState});
    return {...state, commentMap};
};

export const commentModDistinguished = ({commentMap, ...state}, {id, newState}) => {
    commentMap = commentMap.set(id, {...commentMap.get(id), is_moderator: newState});
    return {...state, commentMap};
};

const commentRemove = ({commentMap, ...state}, {id, moderator}) => {
    commentMap = commentMap.set(id, update(commentMap.get(id), { is_removed: { $set: true }, is_approved: { $set: false },
        moderation: { removed_at: { $set: new Date().valueOf() }, removed_by: { $set: moderator }, approved_at : { $set: 0 }, approved_by: { $set: '' }}}));
    return {...state, commentMap};
};

const commentApprove = ({commentMap, ...state}, {id, moderator}) => {
    commentMap = commentMap.set(id, update(commentMap.get(id), { is_removed: { $set: false }, is_approved: { $set: true },
        moderation: { removed_at: { $set: 0 }, removed_by: { $set: '' }, approved_at : { $set: new Date().valueOf() }, approved_by: { $set: moderator }}}));
    return {...state, commentMap};
};

const commentIgnoreReports = ({commentMap, ...state}, {id, ignoreReports}) => {
    commentMap = commentMap.set(id, update(commentMap.get(id), { moderation: { is_reported: { $set: false }, is_ignored_reports: { $set: ignoreReports }}}));
    return {...state, commentMap};
};

export default {
    commentsLoaded,
    commentListLoading,
    commentListLoaded,
    commentListExtended,
    commentListLoadFailed,
    commentCreated,
    commentEdited,
    commentVoteChanged,
    commentDeleted,
    commentModDistinguished,
    commentSticky,
    commentRemove,
    commentApprove,
    commentIgnoreReports,
};
